import { useEffect, useState, type ReactNode } from 'react';
import { ClipboardCopy, Copy, Eye, EyeOff, KeyRound, LogIn, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { CREDENTIAL_KINDS, signsInWith } from '@cp/shared';
import { Banner, Button, Card, ConfirmDialog, EmptyState, IconButton, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { CredentialDialog } from '@/components/sites/CredentialDialog';
import { useCmsSignIn, useCredentials, useDeleteCredential, useRevealCredential } from '@/hooks/useCredentials';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import { label } from '@/utils/labels';
import type { CredentialOwner, SiteCredentialView } from '@/types/api';

/** How long a shown password stays on screen. */
const SHOWN_FOR_MS = 30_000;

/**
 * The ways into what a site depends on (ADR-0031), for the owner and
 * administrators. Passwords are fetched one at a time when asked for — to be
 * seen for half a minute or copied — and each such request is in the trail.
 * A CMS login signs in at a click where the panel knows the form — WordPress's
 * (ADR-0035), or the one the credential describes (ADR-0042); any other opens
 * its sign-in page with the password on the clipboard. A server's credentials
 * (ADR-0058) are only kept and shown: the panel never signs in with them.
 */
export function CredentialsTab({
  owner,
  site,
}: {
  readonly owner: CredentialOwner;
  /** A site's: what its CMS sign-in needs. Absent for a server. */
  readonly site?: { readonly engine: string; readonly platform: string | null; readonly adminUrl: string | null };
}): ReactNode {
  const credentials = useCredentials(owner, true);
  const [editing, setEditing] = useState<SiteCredentialView | 'new' | null>(null);
  const [deleting, setDeleting] = useState<SiteCredentialView | null>(null);
  const [shown, setShown] = useState<{ id: string; password: string } | null>(null);

  useEffect(() => {
    if (!shown) return;
    const timer = setTimeout(() => setShown(null), SHOWN_FOR_MS);
    return () => clearTimeout(timer);
  }, [shown]);

  const reveal = useRevealCredential(owner, { onError: (error) => toast.error('Не удалось получить пароль', errorText(error)) });
  const signIn = useCmsSignIn(owner.id, { onError: (error) => toast.error('Не удалось войти в админку', errorText(error)) });
  const remove = useDeleteCredential(owner, {
    onSuccess: () => {
      toast.success('Доступ удалён');
      setDeleting(null);
    },
  });

  async function show(credential: SiteCredentialView): Promise<void> {
    if (shown?.id === credential.id) return setShown(null);
    const { password } = await reveal.mutateAsync(credential.id);
    setShown({ id: credential.id, password });
  }

  async function copy(text: string, what: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} скопирован`);
    } catch {
      toast.error('Буфер обмена недоступен', 'Скопируйте пароль вручную');
    }
  }

  async function copyPassword(credential: SiteCredentialView): Promise<void> {
    const { password } = shown?.id === credential.id ? shown : await reveal.mutateAsync(credential.id);
    await copy(password, 'Пароль');
  }

  /** Where a login is typed in by hand: the credential's own address, else the site's admin. */
  const signInPage = (credential: SiteCredentialView): string | null => (site ? (credential.url ?? site.adminUrl) : null);

  /** Any CMS: its sign-in page opened, the password on the clipboard — copied first, while this tab still has focus. */
  async function openWithPassword(credential: SiteCredentialView, address: string): Promise<void> {
    await copyPassword(credential);
    window.open(address, '_blank', 'noopener');
  }

  const rows = credentials.data ?? [];

  return (
    <div className="stack">
      <Banner tone="info" icon={ShieldCheck}>
        {owner.kind === 'node'
          ? 'Только для просмотра: панель по ним на сервер не заходит. Доступны владельцу и администраторам, пароли зашифрованы, просмотр и копирование фиксируются в журнале.'
          : 'Доступны владельцу и администраторам. Пароли зашифрованы, просмотр и копирование фиксируются в журнале.'}
      </Banner>

      <Card
        flush
        title="Доступы"
        icon={KeyRound}
        actions={
          <Button size="sm" icon={Plus} onClick={() => setEditing('new')}>
            Добавить
          </Button>
        }
      >
        {credentials.isPending ? (
          <TableSkeleton rows={3} columns={5} />
        ) : credentials.isError ? (
          <ErrorState error={credentials.error} />
        ) : rows.length === 0 ? (
          <EmptyState icon={KeyRound} message="Доступов пока нет" />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Доступ</th>
                  <th scope="col">Адрес</th>
                  <th scope="col">Логин</th>
                  <th scope="col">Пароль</th>
                  <th scope="col">Изменён</th>
                  <th scope="col" aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {rows.map((credential) => (
                  <tr key={credential.id}>
                    <td>
                      <div className="stack stack-tight">
                        <span>{credential.label}</span>
                        <span className="muted text-sm">{label(CREDENTIAL_KINDS, credential.kind)}</span>
                        {credential.notes ? <span className="muted text-sm">{credential.notes}</span> : null}
                      </div>
                    </td>
                    <td>
                      {credential.url ? (
                        <a className="mono link-external" href={credential.url} target="_blank" rel="noreferrer noopener">
                          {credential.url}
                        </a>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td>
                      {credential.login ? (
                        <span className="row-tight">
                          <span className="mono">{credential.login}</span>
                          <IconButton icon={Copy} size="sm" variant="ghost" label="Скопировать логин" onClick={() => void copy(credential.login!, 'Логин')} />
                        </span>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td>
                      {credential.hasPassword ? (
                        <span className="row-tight">
                          <span className="mono">{shown?.id === credential.id ? shown.password : '••••••••'}</span>
                          <IconButton
                            icon={shown?.id === credential.id ? EyeOff : Eye}
                            size="sm"
                            variant="ghost"
                            label={shown?.id === credential.id ? 'Скрыть пароль' : 'Показать пароль на 30 секунд'}
                            disabled={reveal.isPending}
                            onClick={() => void show(credential)}
                          />
                          <IconButton
                            icon={Copy}
                            size="sm"
                            variant="ghost"
                            label="Скопировать пароль"
                            disabled={reveal.isPending}
                            onClick={() => void copyPassword(credential)}
                          />
                        </span>
                      ) : (
                        <span className="faint">не сохранён</span>
                      )}
                    </td>
                    <td className="nowrap">
                      <div className="stack stack-tight">
                        <span title={credential.updatedAt}>
                          {formatRelative(credential.updatedAt)}
                          {credential.updatedBy ? <span className="muted"> · {credential.updatedBy}</span> : null}
                        </span>
                        {credential.revealedAt ? (
                          <span className="muted text-sm">
                            просмотр: {credential.revealedBy ?? '—'}, {formatRelative(credential.revealedAt)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="nowrap">
                      {site && signsInWith(site, credential) ? (
                        <IconButton
                          icon={LogIn}
                          size="sm"
                          variant="ghost"
                          label="Войти в админку с этим доступом"
                          disabled={signIn.busy}
                          onClick={() => signIn.signIn(credential.id)}
                        />
                      ) : credential.kind === 'cms' && credential.hasPassword && signInPage(credential) ? (
                        <IconButton
                          icon={ClipboardCopy}
                          size="sm"
                          variant="ghost"
                          label="Войти (пароль в буфере)"
                          disabled={reveal.isPending}
                          onClick={() => void openWithPassword(credential, signInPage(credential)!)}
                        />
                      ) : null}
                      <IconButton icon={Pencil} size="sm" variant="ghost" label="Изменить" onClick={() => setEditing(credential)} />
                      <IconButton icon={Trash2} size="sm" variant="ghost" label="Удалить" onClick={() => setDeleting(credential)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing ? <CredentialDialog owner={owner} credential={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить доступ"
        description={deleting?.label}
        confirmLabel="Удалить"
        destructive
        busy={remove.isPending}
        error={remove.isError ? errorText(remove.error) : undefined}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onClose={() => {
          remove.reset();
          setDeleting(null);
        }}
      >
        <p className="field-hint">Логин и пароль будут удалены без возможности восстановления.</p>
      </ConfirmDialog>
    </div>
  );
}
